import { Button, Card, Col, Descriptions, Empty, Input, InputNumber, Modal, Radio, Row, Select, Switch, Table, Tabs, Tag, Upload, notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { BiDownload, BiPlus, BiUpload } from "react-icons/bi";
import * as XLSX from "xlsx";
import cuid from "cuid";
import { useNavigate, useParams } from "react-router-dom";
import { BiChevronLeft, BiPause, BiPlay } from "react-icons/bi";
import Header from "shared/components/Header";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { SCREENS } from "utils/constants";
import parseLeadQuery from "./parseLeadQuery";
import SequenceBuilder from "./SequenceBuilder";

const STATE_COLORS = {
  draft: { dot: "#9ca3af", bg: "#f3f4f6", text: "#6b7280", label: "Brouillon" },
  running: { dot: "#27ae60", bg: "#ecfdf5", text: "#065f46", label: "En cours" },
  paused: { dot: "#f2994a", bg: "#fff8ee", text: "#92400e", label: "En pause" },
  completed: { dot: "#6366f1", bg: "#eef2ff", text: "#3730a3", label: "Terminé" },
  archived: { dot: "#9ca3af", bg: "#f3f4f6", text: "#6b7280", label: "Archivé" },
};

const ConfigTab = ({ campaign, senders, schedules, onUpdateCampaign }) => {
  const assignedSenders = (senders || []).filter((s) => (campaign.senderIds || []).includes(s.id));
  const config = get(campaign, "config") || {};

  const saveConfig = async (field, value) => {
    try {
      await onUpdateCampaign({
        variables: {
          where: { id: campaign.id },
          data: { config: { ...config, [field]: value } },
        },
      });
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour" });
    }
  };

  const saveName = async (name) => {
    try {
      await onUpdateCampaign({
        variables: { where: { id: campaign.id }, data: { name } },
      });
    } catch (err) {
      notification.error({ message: "Échec de la mise à jour" });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <Card title="Général" size="small">
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <div>
            <label style={{ fontSize: 12, color: "#6b7280", display: "block", marginBottom: 4 }}>Nom</label>
            <Input
              defaultValue={campaign.name}
              onBlur={(e) => saveName(e.target.value)}
            />
          </div>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="État">
              <Tag style={{ background: STATE_COLORS[campaign.state]?.bg, color: STATE_COLORS[campaign.state]?.text, border: "none" }}>
                {STATE_COLORS[campaign.state]?.label}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Créé le">{new Date(campaign.createdDate).toLocaleDateString("fr-FR")}</Descriptions.Item>
          </Descriptions>
        </div>
      </Card>
      <Card title="Expéditeurs" size="small">
        <div style={{ marginBottom: 12 }}>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Sélectionner des expéditeurs..."
            value={campaign.senderIds || []}
            onChange={async (ids) => {
              try {
                await onUpdateCampaign({
                  variables: { where: { id: campaign.id }, data: { senderIds: ids } },
                });
                notification.success({ message: "Expéditeurs mis à jour" });
              } catch (err) {
                notification.error({ message: "Échec de la mise à jour" });
              }
            }}
          >
            {(senders || []).map((s) => (
              <Select.Option key={s.id} value={s.id}>
                {s.email} ({s.displayName || s.provider})
              </Select.Option>
            ))}
          </Select>
        </div>
        {assignedSenders.length > 0 && (
          <Table dataSource={assignedSenders} rowKey="id" size="small" pagination={false} columns={[
            { title: "Email", dataIndex: "email" },
            { title: "Fournisseur", dataIndex: "provider", width: 100 },
            { title: "Statut", dataIndex: "status", width: 100 },
            { title: "Limite", dataIndex: "dailyLimit", width: 80 },
            { title: "Envoyés", dataIndex: "sentToday", width: 80 },
          ]} />
        )}
      </Card>
      <Card title="Limites & Conditions d'arrêt" size="small">
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div className="d-flex justify--between" style={{ alignItems: "center" }}>
            <span>Limite envois/jour</span>
            <InputNumber size="small" min={1} defaultValue={config.dailySendLimit || 50}
              onBlur={(e) => saveConfig("dailySendLimit", parseInt(e.target.value, 10) || 50)}
              style={{ width: 80 }} />
          </div>
          <div className="d-flex justify--between" style={{ alignItems: "center" }}>
            <span>Max leads en séquence</span>
            <InputNumber size="small" min={0} defaultValue={config.maxLeadsInSequence}
              onBlur={(e) => saveConfig("maxLeadsInSequence", parseInt(e.target.value, 10) || null)}
              style={{ width: 80 }} placeholder="∞" />
          </div>
          <div className="d-flex justify--between" style={{ alignItems: "center" }}>
            <span>Arrêt sur réponse</span>
            <Switch size="small" checked={config.stopOnReply} onChange={(v) => saveConfig("stopOnReply", v)} />
          </div>
          <div className="d-flex justify--between" style={{ alignItems: "center" }}>
            <span>Arrêt sur rebond</span>
            <Switch size="small" checked={config.stopOnBounce} onChange={(v) => saveConfig("stopOnBounce", v)} />
          </div>
          <div className="d-flex justify--between" style={{ alignItems: "center" }}>
            <span>Arrêt sur désinscription</span>
            <Switch size="small" checked={config.stopOnUnsubscribe} onChange={(v) => saveConfig("stopOnUnsubscribe", v)} />
          </div>
          <div className="d-flex justify--between" style={{ alignItems: "center" }}>
            <span>Suivi ouvertures</span>
            <Switch size="small" checked={config.trackOpens} onChange={(v) => saveConfig("trackOpens", v)} />
          </div>
          <div className="d-flex justify--between" style={{ alignItems: "center" }}>
            <span>Suivi clics</span>
            <Switch size="small" checked={config.trackClicks} onChange={(v) => saveConfig("trackClicks", v)} />
          </div>
        </div>
      </Card>
    </div>
  );
};

const LeadsTab = ({ campaign }) => {
  const [filter, setFilter] = useState("all");
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [segmentFilters, setSegmentFilters] = useState({});
  const [segmentText, setSegmentText] = useState("");

  const buildLeadVariables = () => {
    const where = {};
    const gt = {};
    if (segmentFilters.hasContratSante !== undefined) where.hasContratSante = segmentFilters.hasContratSante;
    if (segmentFilters.hasContratPrevoyance !== undefined) where.hasContratPrevoyance = segmentFilters.hasContratPrevoyance;
    if (segmentFilters.hasContratDeces !== undefined) where.hasContratDeces = segmentFilters.hasContratDeces;
    if (segmentFilters.regime) where.regime = segmentFilters.regime;
    if (segmentFilters.departement) where.departement = segmentFilters.departement;
    if (segmentFilters.cotisationMin) gt.cotisationMensuelle = segmentFilters.cotisationMin;
    return {
      where: Object.keys(where).length > 0 ? where : undefined,
      gt: Object.keys(gt).length > 0 ? gt : undefined,
    };
  };

  const { data: allLeadsData, refetch: refetchLeads } = useQuery(graphql.queries.CRM_LEADS, {
    variables: buildLeadVariables(),
    skip: !assignOpen,
    fetchPolicy: "network-only",
  });

  const { data, refetch } = useQuery(graphql.queries.CRM_CAMPAIGN_LEADS, {
    variables: { where: { campaign: { id: campaign.id } } },
    fetchPolicy: "network-only",
  });
  const [updateCampaignLead] = useMutation(graphql.mutations.UPDATE_CRM_CAMPAIGN_LEAD);
  const [deleteCampaignLead] = useMutation(graphql.mutations.DELETE_CRM_CAMPAIGN_LEAD);
  const [createLead] = useMutation(graphql.mutations.CREATE_CRM_LEAD);
  const [createCampaignLead] = useMutation(graphql.mutations.CREATE_CRM_CAMPAIGN_LEAD);

  const handleDownloadTemplate = () => {
    const rows = [
      { Email: "", Prénom: "", Nom: "", Entreprise: "" },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    ws["!cols"] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 20 }];
    XLSX.writeFile(wb, `leads-template-${campaign.name}.xlsx`);
    notification.success({ message: "Modèle téléchargé" });
  };

  const parseExcel = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const workbook = XLSX.read(e.target.result, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        resolve(XLSX.utils.sheet_to_json(sheet));
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const handleImportLeads = async (file) => {
    try {
      const rows = await parseExcel(file);
      let added = 0;
      let skipped = 0;
      const existingEmails = new Set(
        campaignLeads.map((cl) => get(cl, "lead.email", "").toLowerCase()),
      );

      for (const row of rows) {
        const email = (row.Email || row.email || "").toString().trim();
        if (!email || existingEmails.has(email.toLowerCase())) {
          skipped++;
          continue;
        }

        const firstName = row["Prénom"] || row.firstName || row.prenom || null;
        const lastName = row.Nom || row.lastName || row.nom || null;
        const companyName = row.Entreprise || row.company || row.entreprise || null;

        try {
          const leadId = cuid();
          await createLead({
            variables: {
              data: {
                id: leadId,
                email,
                firstName: firstName ? String(firstName) : null,
                lastName: lastName ? String(lastName) : null,
                companyName: companyName ? String(companyName) : null,
              },
            },
          });
          await createCampaignLead({
            variables: {
              data: {
                id: cuid(),
                campaign: { id: campaign.id },
                lead: { id: leadId },
                status: "queued",
              },
            },
          });
          existingEmails.add(email.toLowerCase());
          added++;
        } catch (err) {
          skipped++;
        }
      }

      await refetch();
      notification.success({
        message: `Import terminé : ${added} ajouté(s), ${skipped} ignoré(s)`,
      });
    } catch (err) {
      notification.error({ message: `Échec de l'import : ${err.message}` });
    }
    return false;
  };

  const campaignLeads = get(data, "crmCampaignLeads.data") || [];
  const count = get(data, "crmCampaignLeads.count", 0);

  const statusCounts = campaignLeads.reduce((acc, cl) => {
    acc[cl.status] = (acc[cl.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = filter === "all" ? campaignLeads : campaignLeads.filter((cl) => cl.status === filter);

  const handleBulkAction = async (action, targets) => {
    for (const cl of targets) {
      if (action === "resume") {
        await updateCampaignLead({ variables: { where: { id: cl.id }, data: { status: "in_sequence", errorMessage: null } } });
      } else if (action === "pause") {
        await updateCampaignLead({ variables: { where: { id: cl.id }, data: { status: "paused" } } });
      } else if (action === "skip") {
        await updateCampaignLead({ variables: { where: { id: cl.id }, data: { status: "in_sequence", stuckSince: null } } });
      } else if (action === "remove") {
        await deleteCampaignLead({ variables: { where: { id: cl.id } } });
      }
    }
    await refetch();
    notification.success({ message: `${targets.length} lead(s) mis à jour` });
  };

  const stats = [
    { label: "Total", value: count, color: "#1a9bdb" },
    { label: "En file d'attente", value: statusCounts.queued || 0, color: "#9ca3af" },
    { label: "En séquence", value: statusCounts.in_sequence || 0, color: "#6366f1" },
    { label: "Répondu", value: statusCounts.replied || 0, color: "#27ae60" },
    { label: "Rebonds", value: statusCounts.bounced || 0, color: "#eb5757" },
    { label: "Erreurs", value: statusCounts.error || 0, color: "#eb5757" },
  ];

  const STATUS_LABELS = {
    queued: "En attente", in_sequence: "En séquence", waiting_task: "Tâche en attente",
    replied: "Répondu", interested: "Intéressé", not_interested: "Pas intéressé",
    bounced: "Rebond", unsubscribed: "Désinscrit", completed: "Terminé",
    paused: "En pause", error: "Erreur",
  };

  const STATUS_COLORS = {
    queued: "default", in_sequence: "blue", waiting_task: "orange",
    replied: "green", interested: "green", not_interested: "default",
    bounced: "red", unsubscribed: "default", completed: "purple",
    paused: "orange", error: "red",
  };

  return (
    <div>
      <div className="d-flex justify--end m-bottom-12" style={{ gap: "8px" }}>
        <Button icon={<BiPlus size={14} />} size="small" type="primary" onClick={() => { setAssignOpen(true); setSelectedLeadIds([]); }}>
          Ajouter des leads
        </Button>
      </div>

      <div className="crm-stats-row m-bottom-16">
        {stats.map((s, i) => (
          <Card key={i} size="small" className="crm-stat-card" style={{ flex: 1 }}>
            <div className="crm-stat-value" style={{ color: s.color, fontSize: 20 }}>{s.value}</div>
            <div className="crm-stat-label" style={{ color: s.color, fontSize: 11 }}>{s.label}</div>
          </Card>
        ))}
      </div>

      <div className="m-bottom-12 d-flex justify--between" style={{ alignItems: "center" }}>
        <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)} size="small" buttonStyle="solid">
          <Radio.Button value="all">Tous ({count})</Radio.Button>
          <Radio.Button value="in_sequence">En séquence</Radio.Button>
          <Radio.Button value="waiting_task">Tâche</Radio.Button>
          <Radio.Button value="error">Erreurs</Radio.Button>
          <Radio.Button value="paused">En pause</Radio.Button>
        </Radio.Group>
        <div className="d-flex" style={{ gap: "6px" }}>
          <Button size="small" onClick={() => handleBulkAction("resume", filtered.filter((l) => l.status === "error" || l.status === "paused"))}>
            Reprendre erreurs
          </Button>
          <Button size="small" onClick={() => handleBulkAction("skip", filtered.filter((l) => l.status === "waiting_task"))}>
            Forcer passage
          </Button>
        </div>
      </div>

      <Table dataSource={filtered} rowKey="id" size="small" pagination={{ pageSize: 20 }} columns={[
        { title: "Lead", render: (_, r) => { const l = get(r, "lead", {}); return <div><strong>{l.firstName} {l.lastName}</strong><div style={{ fontSize: 11, color: "#6b7280" }}>{l.email}</div></div>; } },
        { title: "Entreprise", width: 120, render: (_, r) => get(r, "lead.companyName") || "—" },
        { title: "Statut", dataIndex: "status", width: 120, render: (s) => <Tag color={STATUS_COLORS[s]}>{STATUS_LABELS[s] || s}</Tag> },
        { title: "Étape", dataIndex: "currentStepLabel", width: 150, render: (v) => v || "—" },
        { title: "Bloqué depuis", dataIndex: "stuckSince", width: 120, render: (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—" },
        { title: "Erreur", dataIndex: "errorMessage", width: 150, render: (v) => v ? <span style={{ color: "#eb5757", fontSize: 11 }}>{v}</span> : "—" },
        { title: "Dernière activité", dataIndex: "lastActivityAt", width: 120, render: (d) => d ? new Date(d).toLocaleDateString("fr-FR") : "—" },
        {
          title: "", width: 120,
          render: (_, r) => (
            <div className="d-flex" style={{ gap: "4px" }}>
              {(r.status === "error" || r.status === "paused") && (
                <Button size="small" type="link" onClick={() => handleBulkAction("resume", [r])}>Reprendre</Button>
              )}
              {r.status === "waiting_task" && (
                <Button size="small" type="link" onClick={() => handleBulkAction("skip", [r])}>Passer</Button>
              )}
              <Button size="small" type="link" danger onClick={() => handleBulkAction("remove", [r])}>Retirer</Button>
            </div>
          ),
        },
      ]} />

      <Modal
        title="Ajouter des leads à la campagne"
        open={assignOpen}
        onCancel={() => { setAssignOpen(false); setSegmentFilters({}); }}
        width="calc(100vw - 300px)"
        style={{ maxWidth: 1200, top: 20 }}
        okText={`Ajouter ${selectedLeadIds.length} lead(s)`}
        cancelText="Annuler"
        okButtonProps={{ disabled: selectedLeadIds.length === 0 }}
        onOk={async () => {
          const alreadyLinked = new Set(campaignLeads.map((cl) => get(cl, "lead.id")));
          let added = 0;
          for (const leadId of selectedLeadIds) {
            if (alreadyLinked.has(leadId)) continue;
            try {
              await createCampaignLead({
                variables: {
                  data: {
                    id: cuid(),
                    campaign: { id: campaign.id },
                    lead: { id: leadId },
                    status: "queued",
                  },
                },
              });
              added++;
            } catch (err) { /* skip */ }
          }
          setAssignOpen(false);
          setSelectedLeadIds([]);
          await refetch();
          notification.success({ message: `${added} lead(s) ajouté(s)` });
        }}
      >
        <Row gutter={8} className="m-bottom-12" align="middle">
          <Col flex="auto">
            <Input
              placeholder="Décrivez votre segment : ex. adhérents avec contrat santé et cotisation > 90€ sans prévoyance"
              allowClear
              value={segmentText}
              onChange={(e) => setSegmentText(e.target.value)}
              onPressEnter={() => {
                if (!segmentText) { setSegmentFilters({}); setTimeout(() => refetchLeads(), 100); return; }
                setSegmentFilters(parseLeadQuery(segmentText));
                setTimeout(() => refetchLeads(), 100);
              }}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={() => {
                if (!segmentText) { setSegmentFilters({}); setTimeout(() => refetchLeads(), 100); return; }
                setSegmentFilters(parseLeadQuery(segmentText));
                setTimeout(() => refetchLeads(), 100);
              }}
            >
              Filtrer
            </Button>
          </Col>
        </Row>

        {Object.keys(segmentFilters).length > 0 && (
          <div className="crm-filter-chips">
            {segmentFilters.hasContratSante === true && <span className="crm-chip">✓ Contrat santé <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.hasContratSante; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.hasContratSante === false && <span className="crm-chip crm-chip--negative">✗ Sans santé <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.hasContratSante; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.hasContratPrevoyance === true && <span className="crm-chip">✓ Contrat prévoyance <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.hasContratPrevoyance; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.hasContratPrevoyance === false && <span className="crm-chip crm-chip--negative">✗ Sans prévoyance <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.hasContratPrevoyance; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.hasContratDeces === true && <span className="crm-chip">✓ Contrat décès <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.hasContratDeces; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.hasContratDeces === false && <span className="crm-chip crm-chip--negative">✗ Sans décès <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.hasContratDeces; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.cotisationMin && <span className="crm-chip">Cotisation &gt; {segmentFilters.cotisationMin}€ <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.cotisationMin; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.regime && <span className="crm-chip">{segmentFilters.regime.replace(/_/g, " ")} <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.regime; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            {segmentFilters.departement && <span className="crm-chip">Dép. {segmentFilters.departement} <span className="crm-chip__remove" onClick={() => { setSegmentFilters((p) => { const n = { ...p }; delete n.departement; return n; }); setTimeout(() => refetchLeads(), 100); }}>×</span></span>}
            <span style={{ fontSize: 11, color: "#9ca3af", cursor: "pointer", textDecoration: "underline" }} onClick={() => { setSegmentFilters({}); setTimeout(() => refetchLeads(), 100); }}>Tout effacer</span>
          </div>
        )}

        <div className="crm-segment-filters">
          <div className="crm-segment-field">
            <label>Santé</label>
            <Select size="small" allowClear placeholder="—" style={{ width: 70 }}
              value={segmentFilters.hasContratSante}
              onChange={(v) => { setSegmentFilters((p) => ({ ...p, hasContratSante: v })); setTimeout(() => refetchLeads(), 100); }}
            >
              <Select.Option value={true}>Oui</Select.Option>
              <Select.Option value={false}>Non</Select.Option>
            </Select>
          </div>
          <div className="crm-segment-field">
            <label>Prévoyance</label>
            <Select size="small" allowClear placeholder="—" style={{ width: 70 }}
              value={segmentFilters.hasContratPrevoyance}
              onChange={(v) => { setSegmentFilters((p) => ({ ...p, hasContratPrevoyance: v })); setTimeout(() => refetchLeads(), 100); }}
            >
              <Select.Option value={true}>Oui</Select.Option>
              <Select.Option value={false}>Non</Select.Option>
            </Select>
          </div>
          <div className="crm-segment-field">
            <label>Décès</label>
            <Select size="small" allowClear placeholder="—" style={{ width: 70 }}
              value={segmentFilters.hasContratDeces}
              onChange={(v) => { setSegmentFilters((p) => ({ ...p, hasContratDeces: v })); setTimeout(() => refetchLeads(), 100); }}
            >
              <Select.Option value={true}>Oui</Select.Option>
              <Select.Option value={false}>Non</Select.Option>
            </Select>
          </div>
          <div className="crm-segment-field">
            <label>Cotisation &gt;</label>
            <InputNumber size="small" min={0} style={{ width: 65 }} placeholder="€"
              value={segmentFilters.cotisationMin}
              onChange={(v) => { setSegmentFilters((p) => ({ ...p, cotisationMin: v })); setTimeout(() => refetchLeads(), 300); }}
            />
          </div>
          <div className="crm-segment-field">
            <label>Régime</label>
            <Select size="small" allowClear placeholder="—" style={{ width: 130 }}
              value={segmentFilters.regime}
              onChange={(v) => { setSegmentFilters((p) => ({ ...p, regime: v })); setTimeout(() => refetchLeads(), 100); }}
            >
              <Select.Option value="SECURITE_SOCIALE">Sécu. Sociale</Select.Option>
              <Select.Option value="ALSACE_MOSELLE">Alsace-Moselle</Select.Option>
              <Select.Option value="REGIME_SPECIAL">Régime Spécial</Select.Option>
              <Select.Option value="REGIME_SALARIES_AGRICOLES">Agricoles</Select.Option>
              <Select.Option value="SECURITE_SOCIALE_INDEPENDANTS">Indépendants</Select.Option>
            </Select>
          </div>
          <div className="crm-segment-field">
            <label>Dép.</label>
            <Input size="small" style={{ width: 50 }} placeholder="75"
              value={segmentFilters.departement}
              onChange={(e) => { setSegmentFilters((p) => ({ ...p, departement: e.target.value || undefined })); setTimeout(() => refetchLeads(), 300); }}
            />
          </div>
        </div>

        <div className="crm-segment-count">
          <span><strong>{get(allLeadsData, "crmLeads.count", 0)}</strong> lead(s) correspondent</span>
          {selectedLeadIds.length > 0 && <span><strong>{selectedLeadIds.length}</strong> sélectionné(s)</span>}
        </div>

        <Table
          dataSource={(get(allLeadsData, "crmLeads.data") || []).filter(
            (l) => !campaignLeads.some((cl) => get(cl, "lead.id") === l.id),
          )}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10 }}
          rowSelection={{
            selectedRowKeys: selectedLeadIds,
            onChange: setSelectedLeadIds,
          }}
          columns={[
            {
              title: "Lead",
              render: (_, r) => (
                <div>
                  <div style={{ fontWeight: 500 }}>{r.firstName} {r.lastName}</div>
                  <div style={{ fontSize: 11, color: "#6b7280" }}>{r.email}</div>
                </div>
              ),
            },
            { title: "Entreprise", dataIndex: "companyName", width: 120 },
            {
              title: "Contrats", width: 120,
              render: (_, r) => (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {r.hasContratSante && <Tag color="blue" style={{ fontSize: 10 }}>Santé</Tag>}
                  {r.hasContratPrevoyance && <Tag color="green" style={{ fontSize: 10 }}>Prév.</Tag>}
                  {r.hasContratDeces && <Tag color="purple" style={{ fontSize: 10 }}>Décès</Tag>}
                </div>
              ),
            },
            {
              title: "Cotisation", dataIndex: "cotisationMensuelle", width: 80, align: "right",
              render: (v) => v ? <span style={{ fontWeight: 500 }}>{v} €</span> : <span style={{ color: "#9ca3af" }}>—</span>,
            },
            {
              title: "Régime", dataIndex: "regime", width: 110,
              render: (v) => {
                const labels = { SECURITE_SOCIALE: "Sécu. Sociale", ALSACE_MOSELLE: "Alsace-Moselle", REGIME_SPECIAL: "Rég. Spécial", REGIME_SALARIES_AGRICOLES: "Agricoles", SECURITE_SOCIALE_INDEPENDANTS: "Indépendants" };
                return v ? <span className={`crm-regime-label crm-regime-label--${v}`}>{labels[v] || v}</span> : <span style={{ color: "#9ca3af" }}>—</span>;
              },
            },
          ]}
        />
      </Modal>
    </div>
  );
};

const HealthTab = ({ campaign }) => {
  const h = get(campaign, "health") || {};

  const { data: sequenceData } = useQuery(graphql.queries.CRM_SEQUENCE, {
    variables: { where: { id: campaign.sequenceId } },
    skip: !campaign.sequenceId,
  });
  const { data: errorsData } = useQuery(graphql.queries.CRM_ERROR_LOGS, {
    variables: { where: { campaignId: campaign.id }, orderBy: [{ createdDate: "DESC" }] },
  });
  const { data: sendersData } = useQuery(graphql.queries.CRM_SENDERS, { variables: {} });

  const steps = get(sequenceData, "crmSequence.steps") || [];
  const errors = get(errorsData, "crmErrorLogs.data") || [];
  const senders = get(sendersData, "crmSenders.data") || [];
  const assignedSenders = senders.filter((s) => (campaign.senderIds || []).includes(s.id));

  const metrics = [
    { label: "Taux d'ouverture", value: `${h.openRate || 0}%`, color: "#1a9bdb" },
    { label: "Taux de réponse", value: `${h.replyRate || 0}%`, color: "#27ae60" },
    { label: "Taux de rebond", value: `${h.bounceRate || 0}%`, color: h.bounceRate > 5 ? "#eb5757" : "#f2994a" },
  ];

  const TYPE_ICONS = {
    email: "📧", linkedinMessage: "🔗", linkedinInvite: "🔗", linkedinVisit: "🔗",
    call: "📞", whatsapp: "💬", task: "✅", api: "🔌", delay: "⏳", condition: "🔀",
  };

  const rootSteps = steps.filter((s) => !s.parentId).sort((a, b) => a.order - b.order);

  return (
    <div>
      <div className="crm-stats-row m-bottom-16">
        {metrics.map((m, i) => (
          <Card key={i} size="small" className="crm-stat-card" style={{ flex: 1 }}>
            <div className="crm-stat-value" style={{ color: m.color, fontSize: 20 }}>{m.value}</div>
            <div className="crm-stat-label" style={{ color: m.color, fontSize: 11 }}>{m.label}</div>
          </Card>
        ))}
      </div>

      <Card title="Entonnoir par étape" size="small" className="m-bottom-16">
        {rootSteps.length === 0 ? (
          <Empty description="Aucune étape dans la séquence" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {rootSteps.map((step, i) => {
              const config = get(step, "config", {});
              const label = config.label || step.type;
              const icon = TYPE_ICONS[step.type] || "•";
              const stepErrors = errors.filter((e) => e.stepId === step.id);

              return (
                <div key={step.id}>
                  <div style={{
                    padding: "10px 16px",
                    borderLeft: `3px solid ${step.type === "delay" ? "#cbd5e1" : "#1a9bdb"}`,
                    background: i % 2 === 0 ? "#f9fafb" : "#ffffff",
                  }}>
                    <div className="d-flex justify--between">
                      <div>
                        <span style={{ marginRight: 6 }}>{icon}</span>
                        <strong style={{ fontSize: 13 }}>Étape {i + 1}: {label}</strong>
                        <span style={{ fontSize: 11, color: "#6b7280", marginLeft: 8 }}>({step.type})</span>
                      </div>
                    </div>
                    {stepErrors.length > 0 && (
                      <div style={{ fontSize: 11, color: "#eb5757", marginTop: 4 }}>
                        {stepErrors.length} erreur(s)
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {assignedSenders.length > 0 && (
        <Card title="Performance par expéditeur" size="small" className="m-bottom-16">
          <Table dataSource={assignedSenders} rowKey="id" size="small" pagination={false} columns={[
            { title: "Email", dataIndex: "email", render: (e) => <strong>{e}</strong> },
            { title: "Envoyés", dataIndex: "sentToday", width: 80 },
            { title: "Limite", dataIndex: "dailyLimit", width: 80 },
            { title: "Rebond", dataIndex: "bounceRate", width: 80, render: (r) => <span style={{ color: r > 5 ? "#eb5757" : "#27ae60" }}>{r || 0}%</span> },
            { title: "Réputation", dataIndex: "reputation", width: 100, render: (r) => <Tag color={r === "good" ? "green" : r === "warning" ? "orange" : "red"}>{r || "—"}</Tag> },
          ]} />
        </Card>
      )}

      <Card title="Journal d'erreurs" size="small">
        {errors.length === 0 ? (
          <Empty description="Aucune erreur" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table dataSource={errors} rowKey="id" size="small" pagination={{ pageSize: 10 }} columns={[
            { title: "Date", dataIndex: "createdDate", width: 120, render: (d) => d ? new Date(d).toLocaleString("fr-FR") : "—" },
            { title: "Lead", dataIndex: "leadEmail", width: 150 },
            { title: "Étape", dataIndex: "stepLabel", width: 150 },
            { title: "Type", dataIndex: "errorType", width: 100, render: (t) => <Tag color="red">{t}</Tag> },
            { title: "Message", dataIndex: "errorMessage" },
          ]} />
        )}
      </Card>
    </div>
  );
};

const Detail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data, loading } = useQuery(graphql.queries.CRM_CAMPAIGN, {
    variables: { where: { id } },
    fetchPolicy: "network-only",
  });
  const { data: sendersData } = useQuery(graphql.queries.CRM_SENDERS, { variables: {} });
  const { data: schedulesData } = useQuery(graphql.queries.CRM_SENDING_SCHEDULES, { variables: {} });
  const [updateCampaign] = useMutation(graphql.mutations.UPDATE_CRM_CAMPAIGN);

  const campaign = get(data, "crmCampaign");
  const senders = get(sendersData, "crmSenders.data") || [];
  const schedules = get(schedulesData, "crmSendingSchedules.data") || [];

  if (loading) return "Chargement...";
  if (!campaign) return <Empty description="Campagne non trouvée" />;

  const handleToggleState = async () => {
    const newState = campaign.state === "draft" ? "running"
      : campaign.state === "running" ? "paused"
        : campaign.state === "paused" ? "running" : null;
    if (!newState) return;
    await updateCampaign({
      variables: {
        where: { id },
        data: {
          state: newState,
          ...(newState === "running" && !campaign.launchedAt ? { launchedAt: new Date().toISOString() } : {}),
          ...(newState === "paused" ? { pausedAt: new Date().toISOString() } : {}),
        },
      },
    });
    window.location.reload();
  };

  const stateAction = campaign.state === "draft"
    ? { text: "Lancer", icon: <BiPlay size={18} />, type: "primary" }
    : campaign.state === "running"
      ? { text: "Pause", icon: <BiPause size={18} />, type: "warning--outlined" }
      : campaign.state === "paused"
        ? { text: "Reprendre", icon: <BiPlay size={18} />, type: "primary" }
        : null;

  return (
    <>
      <Header
        key="header"
        title={campaign.name}
        actions={[
          { text: "Retour", onClick: () => navigate(SCREENS.PRIVATE.ADMIN.CAMPAIGNS.LIST.path), type: "primary--outlined", icon: <BiChevronLeft size={20} /> },
          ...(stateAction ? [{ text: stateAction.text, onClick: handleToggleState, type: stateAction.type, icon: stateAction.icon }] : []),
        ]}
      />
      <Tabs
        defaultActiveKey="sequence"
        items={[
          { key: "config", label: "Configuration", children: <ConfigTab campaign={campaign} senders={senders} schedules={schedules} onUpdateCampaign={updateCampaign} /> },
          { key: "sequence", label: "Séquence", children: <SequenceBuilder sequenceId={campaign.sequenceId} campaignState={campaign.state} /> },
          { key: "leads", label: "Leads", children: <LeadsTab campaign={campaign} /> },
          { key: "health", label: "Santé & Stats", children: <HealthTab campaign={campaign} /> },
        ]}
      />
    </>
  );
};

export default Detail;
